import React, { useEffect, useState } from 'react';
import { ClipboardCheck, AlertCircle, X } from 'lucide-react';
import { useVerificationWizard } from './useVerificationWizard';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import type { WizardStepInfo } from '../wizard/WizardStepIndicator';
import WizardShell from '../wizard/core/WizardShell';
import { VERIFICATION_STEP_NAMES, VERIFICATION_TOTAL_STEPS } from '../../types/verification';
import '../../pages/VerificationWizardPage.css';

import VerifyBasicInfoStep from './steps/VerifyBasicInfoStep';
import VerifyAddressStep from './steps/VerifyAddressStep';
import VerifyContactsStep from './steps/VerifyContactsStep';
import VerifyOperatingHoursStep from './steps/VerifyOperatingHoursStep';
import VerifyCapabilitiesStep from './steps/VerifyCapabilitiesStep';
import VerifyServicesStep from './steps/VerifyServicesStep';
import VerifyProceduresStep from './steps/VerifyProceduresStep';
import VerifyReviewStep from './steps/VerifyReviewStep';
import { useAuth } from '@shared/context/useAuth';

function getStepComponents(isAdmin: boolean) {
  return [
    VerifyBasicInfoStep,
    VerifyAddressStep,
    VerifyOperatingHoursStep,
    VerifyContactsStep,
    VerifyCapabilitiesStep,
    isAdmin ? VerifyServicesStep : VerifyProceduresStep,
    VerifyReviewStep,
  ];
}

function getStepNames(isAdmin: boolean) {
  const servicesLabel = isAdmin ? 'Services & Pricing' : 'Procedures';
  return VERIFICATION_STEP_NAMES.map((name, i) =>
    i === 5 ? servicesLabel : name
  );
}

interface VerificationWizardContentProps {
  onClose?: () => void;
  onComplete?: () => void;
}

const VerificationWizardContent: React.FC<Readonly<VerificationWizardContentProps>> = ({ onClose, onComplete }) => {
  const {
    state,
    goToStep,
    goNext,
    goPrevious,
    completeVerification,
    isSaving,
    isLoading,
    error,
    clinic,
    saveError,
    clearSaveError,
  } = useVerificationWizard();

  const { user } = useAuth();
  // SLV2-3452: fee entry (Services & Pricing) is for Admin / SysAdmin — the rule the API enforces
  const isAdmin = (user?.roles.includes('Admin') || user?.roles.includes('SysAdmin')) ?? false;
  const stepComponents = getStepComponents(isAdmin);
  const stepNames = getStepNames(isAdmin);

  const { currentStep } = state;
  const isLastStep = currentStep === VERIFICATION_TOTAL_STEPS - 1;

  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const leave = onClose || (() => window.history.back());

  // The clinic's data as loaded, to tell typed-but-unsaved edits apart from it.
  const enteredData = JSON.stringify({ ...state, currentStep: 0, stepStatuses: null });
  const [loadedData, setLoadedData] = useState<string | null>(null);
  if (!isLoading && loadedData === null) setLoadedData(enteredData);

  // Each step saves only through its own button, and the step outcomes that
  // Complete Verification submits live only here. Never leave mid-save, and ask
  // before leaving once a field has been edited or any step has been
  // confirmed, updated or skipped (SLV2-4049).
  const requestClose = () => {
    if (isSaving) return;
    const inProgress =
      Object.values(state.stepStatuses).some((s) => s && s.status !== 'pending') ||
      (loadedData !== null && enteredData !== loadedData);
    if (inProgress) setConfirmDiscard(true);
    else leave();
  };

  useEffect(() => {
    // Escape only applies to the modal (onClose given), not the full page.
    if (!onClose || isLoading || confirmDiscard) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  });

  // SLV2-3155: the modal mounts this content immediately, before the clinic
  // load resolves. Until it does, the wizard's state is the empty template —
  // rendering the steps then would present "no hours on record" (and enabled
  // Save buttons) for a clinic that may have real data, and a quick save
  // could overwrite it with blanks. Gate everything on the initial load.
  if (isLoading) {
    return (
      <div className="verification-load-panel" role="status">
        <div className="loading-spinner" />
        <p>Loading clinic data…</p>
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="verification-load-panel" role="alert">
        <p>{error || 'Failed to load clinic data.'}</p>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Back to Queue
        </button>
      </div>
    );
  }

  // Build step info for the indicator
  const steps: WizardStepInfo[] = stepNames.map((name, i) => {
    const status = state.stepStatuses[i]?.status || 'pending';
    return {
      name,
      completed: status === 'confirmed' || status === 'updated',
      hasErrors: false,
      skipped: status === 'skipped',
    };
  });

  // Track visited steps as boolean array
  const visitedSteps: boolean[] = Array.from({ length: VERIFICATION_TOTAL_STEPS }, (_, i) => {
    return state.stepStatuses[i]?.status !== 'pending' || i <= currentStep;
  });

  const handleFinish = async () => {
    // completeVerification resolves false on failure (surfaced via saveError) —
    // the wizard must not close and report success when the verify call failed.
    const succeeded = await completeVerification();
    if (succeeded) {
      onComplete?.();
    }
  };

  const StepComponent = stepComponents[currentStep];

  // Calculate last verified display
  const lastVerifiedDisplay = clinic?.lastVerifiedDate
    ? new Date(clinic.lastVerifiedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Never';

  const headerContent = (
    <div className="verification-header-content">
      <div className="verification-clinic-info">
        <span className="wizard-header-title">{clinic?.name || 'Clinic Verification'}</span>
        {clinic?.code && <span className="clinic-code-badge">{clinic.code}</span>}
      </div>
      <div className="verification-meta">
        <span className="meta-label">Last Verified:</span>
        <span className={`meta-value ${!clinic?.lastVerifiedDate ? 'never' : ''}`}>
          {lastVerifiedDisplay}
        </span>
      </div>
    </div>
  );

  return (
    <>
      <WizardShell
        headerContent={headerContent}
        steps={steps}
        currentStep={currentStep}
        visitedSteps={visitedSteps}
        onStepClick={goToStep}
        totalSteps={VERIFICATION_TOTAL_STEPS}
        canGoNext={currentStep < VERIFICATION_TOTAL_STEPS - 1}
        canGoPrevious={currentStep > 0}
        isLastStep={isLastStep}
        isSubmitting={isSaving}
        onPrevious={goPrevious}
        onNext={goNext}
        onFinish={handleFinish}
        onCancel={requestClose}
        navLabels={{
          finishLabel: 'Complete Verification',
          finishIcon: <ClipboardCheck size={18} />,
          submittingLabel: 'Verifying...',
          cancelLabel: 'Back to Queue',
        }}
      >
        {/* SLV2-3155: failed saves used to vanish silently — surface them here,
            above whichever step is active. */}
        {saveError && (
          <div className="verification-save-error" role="alert">
            <AlertCircle size={16} />
            <span>{saveError}</span>
            <button
              type="button"
              className="verification-save-error-dismiss"
              onClick={clearSaveError}
              aria-label="Dismiss error"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <StepComponent />
      </WizardShell>

      <ConfirmationModal
        isOpen={confirmDiscard}
        title="Leave this verification?"
        message="The verification is not complete. Changes already saved on a step stay saved, but unsaved edits and the step progress are lost, and the clinic is not marked as verified."
        confirmText="Leave"
        cancelText="Keep Verifying"
        danger
        onConfirm={leave}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>
  );
};

export default VerificationWizardContent;
